import { useLocation, useNavigate } from 'react-router-dom';
import { Helmet } from 'react-helmet-async';

const Row = ({ icon, label, value }) => (
  <div className="flex items-start gap-3">
    <div className="mt-0.5">{icon}</div>
    <div className="text-sm">
      <div className="text-gray-500">{label}</div>
      <div className="font-semibold text-gray-800 break-all">{value || '-'}</div>
    </div>
  </div>
);

const QuoteSuccess = () => {
  const navigate = useNavigate();
  const { state } = useLocation();
  const form = state?.form || {};
  const selectedCount = state?.selectedCount || 0;

  const from = form.fromAddress ? `${form.fromAddress}${form.fromCity ? ', ' + form.fromCity : ''}` : '';
  const to = form.toAddress ? `${form.toAddress}${form.toCity ? ', ' + form.toCity : ''}` : '';

  return (
    <div className="min-h-[calc(100vh-8rem)] bg-yellow-50/40 py-10 md:py-14">
      <Helmet>
        <title>Quote Submitted – MyMoveAdvisor</title>
        <meta name="robots" content="noindex" />
      </Helmet>
      <div className="container mx-auto px-4">
        <div className="max-w-2xl mx-auto bg-white rounded-2xl border border-yellow-200 shadow-[0_10px_35px_rgba(0,0,0,0.08)] p-6 md:p-8">
          <div className="flex items-center justify-center mb-3">
            <div className="w-14 h-14 rounded-full bg-green-100 text-green-600 flex items-center justify-center shadow-lg shadow-green-100">
              <svg xmlns="http://www.w3.org/2000/svg" width="28" height="28" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="3" stroke-linecap="round" stroke-linejoin="round"><path d="M20 6 9 17l-5-5" /></svg>
            </div>
          </div>
          <h1 className="text-center text-xl md:text-2xl font-bold text-gray-900">Quote Request Sent Successfully!</h1>
          <p className="text-center text-gray-600 mt-2 mb-6">Your quote request has been sent to {selectedCount || 2} professional movers in your area.</p>

          <div className="rounded-xl border border-blue-100 bg-blue-50 p-4 mb-5">
            <div className="flex items-start gap-3">
              <div className="text-blue-600 mt-0.5">
                <svg xmlns="http://www.w3.org/2000/svg" width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"><circle cx="12" cy="12" r="10" /><path d="M12 16v-4" /><path d="M12 8h.01" /></svg>
              </div>
              <div>
                <div className="font-semibold text-blue-800 text-sm">What happens next?</div>
                <div className="text-sm text-blue-700">Selected movers will review your request and send you personalized quotes within 24–48 hours. You'll receive notifications via email and SMS.</div>
              </div>
            </div>
          </div>

          <div className="rounded-xl border border-gray-200 p-4 md:p-5 mb-6">
            <div className="font-semibold text-gray-800 mb-4">Request Summary</div>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-5">
              <Row icon={<svg xmlns="http://www.w3.org/2000/svg" width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round" className="text-orange-500"><path d="M20 10c0 6-8 12-8 12s-8-6-8-12a8 8 0 0 1 16 0Z" /><circle cx="12" cy="10" r="3" /></svg>} label="Moving From" value={from} />
              <Row icon={<svg xmlns="http://www.w3.org/2000/svg" width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round" className="text-orange-500"><path d="M20 10c0 6-8 12-8 12s-8-6-8-12a8 8 0 0 1 16 0Z" /><circle cx="12" cy="10" r="3" /></svg>} label="Moving To" value={to} />
              <Row icon={<svg xmlns="http://www.w3.org/2000/svg" width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round" className="text-blue-500"><rect width="20" height="16" x="2" y="4" rx="2" /><path d="m22 7-8.97 5.7a1.94 1.94 0 0 1-2.06 0L2 7" /></svg>} label="Email" value={form.email} />
              <Row icon={<svg xmlns="http://www.w3.org/2000/svg" width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round" className="text-green-500"><path d="M22 16.92v3a2 2 0 0 1-2.18 2 19.79 19.79 0 0 1-8.63-3.07 19.5 19.5 0 0 1-6-6 19.79 19.79 0 0 1-3.07-8.67A2 2 0 0 1 4.11 2h3a2 2 0 0 1 2 1.72 12.84 12.84 0 0 0 .7 2.81 2 2 0 0 1-.45 2.11L8.09 9.91a16 16 0 0 0 6 6l1.27-1.27a2 2 0 0 1 2.11-.45 12.84 12.84 0 0 0 2.81.7A2 2 0 0 1 22 16.92z" /></svg>} label="Phone" value={form.phone} />
              <Row icon={<svg xmlns="http://www.w3.org/2000/svg" width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round" className="text-orange-600"><rect width="16" height="13" x="2" y="6" rx="2" /><path d="M16 8h4l3 3v5h-7V8z" /><circle cx="9" cy="18" r="2" /><circle cx="17" cy="18" r="2" /></svg>} label="Move Type" value={form.moveCategory || 'Residential Move'} />
            </div>
          </div>

          <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
            <button
              className="w-full bg-gradient-to-r from-orange-400 to-orange-600 text-white rounded-lg py-2.5 font-semibold"
              onClick={() => {
                // Save the quote request to localStorage
                try {
                  const existing = JSON.parse(localStorage.getItem('myRequests') || '[]');
                  const req = {
                    id: `#RQ-${Date.now().toString().slice(-6)}`,
                    date: new Date().toISOString().slice(0, 10),
                    name: [form.firstName, form.lastName].filter(Boolean).join(' ').trim() || 'User',
                    email: form.email || '',
                    from: {
                      address: form.fromAddress || '',
                      city: form.fromCity || '',
                      zip: form.fromZip || '',
                    },
                    to: {
                      address: form.toAddress || '',
                      city: form.toCity || '',
                      zip: form.toZip || '',
                    },
                    category: form.moveCategory || 'Residential Move',
                    moveType: form.moveType || '',
                    assistance: form.assistance || '',
                    property: form.property || '',
                    status: 'Submitted',
                    selectedMovers: (state?.movers || []).map(m => m.name || m),
                    assignedMovers: ['Family First Movers'], // Admin recommendation placeholder
                  };
                  localStorage.setItem('myRequests', JSON.stringify([req, ...existing]));
                } catch (e) {
                }
                navigate('/user');
              }}
            >
              View My Quotes Dashboard
            </button>
            <button
              className="w-full border border-gray-300 text-gray-700 rounded-lg py-2.5 font-semibold"
              onClick={() => navigate('/')}
            >
              Back to Home
            </button>
          </div>

          <div className="text-center text-xs text-gray-500 mt-5">
            Need help? Contact our support team at <a className="text-orange-600" href="mailto:support@mymoveadvisor.com">support@mymoveadvisor.com</a>
          </div>
        </div>
      </div>
    </div>
  );
};

export default QuoteSuccess;
